


























































































































import {
  computed,
  defineComponent,
  nextTick,
  PropType,
  ref,
  watch
} from '@vue/composition-api';
import { AxiosError } from 'axios';
import {
  useCreateTariffRegion,
  useUpdateTariffRegion
} from '@/composable/tariffs';

import { cloneDeep } from 'lodash';
import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';
import {
  ITariffRegion,
  ITariffRegionData,
  TariffRegion,
  ICountry,
  ICountryData,
  Country
} from '@/models';
import { ISystemError } from '@/models/error/error.types';
import { ALForm } from '@/common/component.types';
import { requiredValidator } from '@/utils/validators';
import { getPropertyFromItem } from '@/utils/helpers';

import { countries } from '@/data/countries';

import ErrorAlert from '@/components/common/ErrorAlert.vue';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import CountryPicker from '@/components/common/CountryPicker.vue';

export default defineComponent({
  name: 'TariffRegionFormDialog',
  components: {
    ALDialog,
    CountryPicker,
    ErrorAlert
  },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    tariffRegion: {
      type: Object as PropType<ITariffRegion | null>,
      default: null
    }
  },
  emits: ['update:dialog'],
  setup(props, { emit, root }) {
    const localDialog = computed({
      get: () => props.dialog,
      set: value => {
        emit('update:dialog', value);
      }
    });

    const tariffRegionData = ref<ITariffRegionData>({
      countries: []
    });

    watch(localDialog, value => {
      if (value) {
        resetFormValidation();
        tariffRegionData.value = !props.isEdit
          ? ({
              countries: []
            } as ITariffRegionData)
          : cloneDeep(props.tariffRegion!);
      }
    });

    const formRef = ref<ALForm | null>(null);
    const valid = ref(false);

    const subtitle = computed(
      () => `${props.isEdit ? 'Update' : 'Create'} tariff region information`
    );

    const computedRegionCountries = computed(() => {
      let countryList: ICountry[] = [];

      countryList = tariffRegionData.value.countries!.map(
        (country: ICountryData | string) => {
          if (typeof country === 'string') {
            return countries().find(
              localCountry => localCountry.code === country
            )!;
          } else {
            return new Country(country);
          }
        }
      );

      return countryList;
    });

    const countryAutoCompletePickerRef = ref<
      InstanceType<typeof CountryPicker>
    >();

    function removeRegionCountry(countryCode: string) {
      // country may be a string or an object depending if the value is coming from the server or locally
      const countryIndex = tariffRegionData.value.countries!.findIndex(
        country => {
          const countryId =
            typeof country === 'string'
              ? country
              : getPropertyFromItem(country, 'code', country);
          return countryId === countryCode;
        }
      );

      tariffRegionData.value.countries!.splice(countryIndex, 1);
    }

    function closeDialog() {
      clearErrors();
      localDialog.value = false;
    }

    const {
      isLoading: isUpdatePending,
      error: updateError,
      mutate: updateTariffRegion,
      reset: updateReset
    } = useUpdateTariffRegion();
    const {
      isLoading: isCreatePending,
      error: createError,
      mutate: createTariffRegion,
      reset: createReset
    } = useCreateTariffRegion();

    const localError = ref<ISystemError | null>(null);

    const loading = computed(() =>
      props.isEdit ? isUpdatePending.value : isCreatePending.value
    );

    const error = computed<ISystemError | null>({
      get() {
        let systemError: ISystemError | null = localError.value;
        const axiosError = props.isEdit ? updateError.value : createError.value;

        if (axiosError) {
          systemError = setErrorFromAxiosError(axiosError as AxiosError);
        }

        return systemError;
      },
      set(value: ISystemError | null) {
        localError.value = value;
      }
    });

    function clearErrors() {
      props.isEdit ? updateReset() : createReset();
      error.value = null;
    }

    function resetFormValidation(): void {
      if (formRef.value) {
        return formRef.value.resetValidation();
      }
    }

    function validateForm(): boolean {
      let valid = false;
      if (formRef.value) {
        valid = formRef.value.validate();
      }
      return valid;
    }

    async function save() {
      clearErrors();
      const valid = await validateForm();

      if (valid) {
        let tariffRegionInstance: ITariffRegion | null = null;

        try {
          tariffRegionInstance = new TariffRegion(tariffRegionData.value);
        } catch (e) {
          // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
          error.value = e as ISystemError;
        }

        if (tariffRegionInstance != null) {
          const saveAction = props.isEdit
            ? updateTariffRegion
            : createTariffRegion;

          let payload = tariffRegionInstance;
          if (props.isEdit) {
            payload = diffDeep(tariffRegionInstance, props.tariffRegion, true);
          }

          saveAction(payload, {
            onSuccess: () => {
              closeDialog();
            }
          });
        }
      }
    }

    return {
      // refs
      countryAutoCompletePickerRef,

      localDialog,
      tariffRegionData,
      valid,
      formRef,
      subtitle,
      closeDialog,
      save,
      clearErrors,
      loading,
      error,
      requiredValidator,
      computedRegionCountries,
      removeRegionCountry
    };
  }
});
